<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA ;
    import { createEventDispatcher } from 'svelte';
    import { __ } from '@oat-sa-private/ui-core';
    import { breakpoints } from '@oat-sa-private/ui-identity';
    import { Icon } from '@oat-sa-private/ui-elements';
    import PageButton from './PageButton.svelte';
    import PageSelectButton from './PageSelectButton.svelte';

    export let totalPages = 5;
    export let currentPage = 1;

    let windowWidth;
    let isMobile;
    let numberOfDisplayedButtons = 9; //Number of pages buttons displayed (numeric and dots)

    $: {
        if (windowWidth <= breakpoints.width.medium) {
            isMobile = true;
            numberOfDisplayedButtons = 1;
        } else {
            isMobile = false;
            numberOfDisplayedButtons = 9;
        }
    }

    /**
     * Creates an array of numbers (positive and/or negative) progressing from
     * offset up to, but not including offset + size.
     *
     * @param {number} size - list length
     * @param {number} offset - start value of array
     *
     * @returns {Array} - list of numbers
     */
    function range(size, offset = 0) {
        return [...Array(size).keys()].map(k => k + offset);
    }

    let canMoveLeft = false; //If we need left dots
    let canMoveRight = false; //If we need right dots
    let pagesToDisplay; //Array of numbers with page numbers

    $: hiddenPages = Math.max(totalPages - numberOfDisplayedButtons, 0); //Numeric page buttons which don't fit the screen
    $: nextDisabled = currentPage === totalPages;
    $: lastDisabled = nextDisabled;
    $: numPages = Math.min(numberOfDisplayedButtons, totalPages); //Number of displayed buttons with page numbers

    $: {
        if (isMobile) {
            canMoveRight = currentPage < totalPages;
            canMoveLeft = currentPage > 1;
            pagesToDisplay = [currentPage];
        } else {
            if (hiddenPages === 0) {
                //All page numbers are rendered, no need of dots
                pagesToDisplay = range(Math.max(numPages - 2, 0), 2);
            } else if (currentPage < numPages - 4) {
                //Beginning of pagination, right dots are rendered
                canMoveLeft = false;
                canMoveRight = true;
                pagesToDisplay = range(numPages - 3, 2);
            } else if (currentPage > totalPages - numPages + 4) {
                //End of pagination, left dots are rendered
                canMoveLeft = true;
                canMoveRight = false;
                pagesToDisplay = range(numPages - 3, totalPages - numPages + 3);
            } else {
                //Both left and right dots are rendered
                canMoveLeft = true;
                canMoveRight = true;
                pagesToDisplay = range(numPages - 4, currentPage - 2);
            }
        }
    }

    const dispatch = createEventDispatcher();

    function changePageByNativeSelect(e) {
        dispatch('changePage', { page: +e.target.value });
    }

    function changePage(e) {
        const page = +e.target.getAttribute('data-page');

        dispatch('changePage', { page });
    }

    function incPage() {
        dispatch('changePage', { page: currentPage + 1 });
    }

    function decPage() {
        dispatch('changePage', { page: currentPage - 1 });
    }

    function goToFirstPage() {
        dispatch('changePage', { page: 1 });
    }

    function goToLastPage() {
        dispatch('changePage', { page: totalPages });
    }
</script>

<style>
    .pagination {
        display: flex;
        list-style: none;
        padding: 0.5rem;
    }

    .pagination-item {
        width: 5.5rem;
        height: 5.5rem;
        text-align: center;
        flex-shrink: 0;

        &:not(.icon) {
            margin-inline-end: 1rem;
        }

        &.previous {
            margin-inline-end: 2rem;
        }

        &.next {
            margin-inline-start: 1rem; /*1x + 1x*/
        }
    }

    .dots {
        @add-mixin bold;
        line-height: 4.5rem;
        font-size: var(--fontsize-body);
    }
</style>

<svelte:window bind:innerWidth={windowWidth} />

<div class="pagination-container">
    <ul class="pagination">
        <li class="pagination-item icon">
            <PageButton hiddenText={__('first page')} disabled={currentPage === 1} on:click={goToFirstPage}>
                <Icon name="chevron-bar-left-16" ariaHidden rtlFlip={true} />
            </PageButton>
        </li>

        <li class="pagination-item icon previous">
            <PageButton hiddenText={__('previous page')} disabled={currentPage === 1} on:click={decPage}>
                <Icon name="chevron-left-16" ariaHidden rtlFlip={true} />
            </PageButton>
        </li>

        {#if !isMobile}
            {#if 1 === currentPage}
                <li class="pagination-item">
                    <PageSelectButton {currentPage} {totalPages} on:change={changePageByNativeSelect} />
                </li>
            {:else}
                <li class="pagination-item">
                    <PageButton ariaCurrent={1 === currentPage ? 'page' : null} on:click={goToFirstPage}>1</PageButton>
                </li>
            {/if}
        {/if}

        {#if canMoveLeft && hiddenPages}
            <li class="pagination-item">
                <div class="dots">...</div>
            </li>
        {/if}
        {#each pagesToDisplay as page}
            <li class="pagination-item num">
                {#if page === currentPage}
                    <PageSelectButton {currentPage} {totalPages} on:change={changePageByNativeSelect} />
                {:else}
                    <PageButton
                        ariaCurrent={page === currentPage ? 'page' : null}
                        dataPage={page}
                        on:click={changePage}>
                        {page}
                    </PageButton>{/if}
            </li>
        {/each}

        {#if canMoveRight && hiddenPages}
            <li class="pagination-item">
                <div class="dots">...</div>
            </li>
        {/if}

        {#if !isMobile && totalPages > 1}
            {#if totalPages === currentPage}
                <li class="pagination-item">
                    <PageSelectButton {currentPage} {totalPages} on:change={changePageByNativeSelect} />
                </li>
            {:else}
                <li class="pagination-item">
                    <PageButton ariaCurrent={totalPages === currentPage ? 'page' : null} on:click={goToLastPage}>
                        {totalPages}
                    </PageButton>
                </li>
            {/if}
        {/if}

        <li class="pagination-item icon next">
            <PageButton hiddenText={__('next page')} on:click={incPage} disabled={nextDisabled}>
                <Icon name="chevron-right-16" ariaHidden rtlFlip={true} />
            </PageButton>
        </li>

        <li class="pagination-item icon">
            <PageButton hiddenText={__('last page')} on:click={goToLastPage} disabled={lastDisabled}>
                <Icon name="chevron-bar-right-16" ariaHidden rtlFlip={true} />
            </PageButton>
        </li>
    </ul>
</div>
